// infrastructure/mappers/billingItemMapper.js

/** Firestore Timestamps arrive as objects with toDate(); anything else is left as it is. */
const toDate = (value) => (typeof value?.toDate === 'function' ? value.toDate() : value ?? null);

const asArray = (value) => (Array.isArray(value) ? value : []);

/**
 * A stored billing item as the worklist reads it.
 *
 * FIELD BY FIELD RATHER THAN A SPREAD, and the reason is the same one the projector's own reader
 * gives: the document carries provenance the screen has no business rendering — the derivation key,
 * the rule audit, the claim-tier hooks — and spreading them in makes every one of them look like
 * something a row may use. A field that matters here is a field named here.
 *
 * IT FILLS IN NOTHING IT CANNOT READ. A missing charge stays null rather than becoming zero: a
 * clinic that has not priced a code is a held item with a reason, and a zero charge would present
 * that as a service worth nothing.
 *
 * @param {Object} document - as stored, with `id` attached by the db layer
 * @returns {import('../../domain/model/BillingItem.types').BillingItem}
 */
// eslint-disable-next-line import/prefer-default-export
export function toBillingItem(document) {
  return {
    id: document.id,
    sessionActivityId: document.sessionActivityId ?? null,
    serviceDate: document.serviceDate ?? null,
    clinicId: document.clinicId ?? null,
    clientId: document.clientId ?? null,
    fundingSourceId: document.fundingSourceId ?? null,
    coverageId: document.coverageId ?? null,
    memberId: document.memberId ?? null,
    policyholderRelationship: document.policyholderRelationship ?? null,
    billingCode: document.billingCode ?? null,
    modifiers: asArray(document.modifiers),
    placeOfServiceCode: document.placeOfServiceCode ?? null,
    renderingProviderId: document.renderingProviderId ?? null,
    renderingProviderNpi: document.renderingProviderNpi ?? null,
    billedByGroup: document.billedByGroup === true,
    unitBasis: document.unitBasis ?? null,
    serviceStart: toDate(document.serviceStart),
    serviceEnd: toDate(document.serviceEnd),
    serviceTimezone: document.serviceTimezone ?? null,
    billableDuration: document.billableDuration ?? null,
    projectedUnits: document.projectedUnits ?? null,
    chargePerUnit: document.chargePerUnit ?? null,
    diagnosisCodes: asArray(document.diagnosisCodes),
    status: document.status ?? null,
    holdReasons: asArray(document.holdReasons),
    exportState: document.exportState ?? 'never',
    lastExportTimestamp: toDate(document.lastExportTimestamp),
  };
} // toBillingItem()
